/**
 * useContentLikedUsers Hook
 *
 * State orchestration for the owner-only "users who liked this" sheet: the
 * initial page load, infinite-scroll pagination with fetch guards, the
 * `!hasMore` early-return, and unique-append dedup by `userId`.
 *
 * Ports Flutter's `ContentUsersLikedBottomSheetState` (`getData()` + the
 * post-frame initial load) from
 * `lib/presentation/bottom_sheet/content_users_liked_bottom_sheet.dart:36-116`.
 *
 * Business logic lives here, not in the owning sheet component. The hook calls
 * `fetchContentLikedUsersUseCase` (never the service / Firestore directly) and
 * reads the current user from the auth slice so the use case can drop blocked
 * rows.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import type { PageCursor } from '@/data/types/json';
import type { ContentLikedUserEntity } from '@/domain/entities/contentReaction/contentLikedUserEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { fetchContentLikedUsersUseCase } from '@/domain/usecases/contentReaction/fetchContentLikedUsersUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { RootState } from '@/presentation/store';

export interface UseContentLikedUsersParams {
  mediaPath: string;
}

export interface UseContentLikedUsersResult {
  likedUsers: ContentLikedUserEntity[];
  hasMore: boolean;
  isFetching: boolean;
  /** True until the first fetch resolves; the sheet shows a centered loader. */
  isFirstLoad: boolean;
  loadMore: () => void;
}

export function useContentLikedUsers({
  mediaPath,
}: UseContentLikedUsersParams): UseContentLikedUsersResult {
  const [likedUsers, setLikedUsers] = useState<ContentLikedUserEntity[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Read the current user from the auth slice and mirror it into a ref so the
  // async fetch closure always reads the latest value without re-subscribing.
  // The use case applies the blocked filter against this user (Flutter
  // `currentUser.usersBlockedMe`, users_data_source.dart:281-284).
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const currentUserRef = useRef<UserEntity | null>(currentUser);
  currentUserRef.current = currentUser;

  // Refs mirror the synchronous Flutter fields (`_lastFetchedItem`, `_hasMore`).
  // React state updates are async, so the fetch reads the refs to make its
  // guards and cursor handling reliable across rapid pagination calls.
  const cursorRef = useRef<PageCursor | undefined>(undefined);
  const hasMoreRef = useRef(true);
  const isFetchingRef = useRef(false);

  // Ports Flutter `getData` (content_users_liked_bottom_sheet.dart:89-116).
  const loadMore = useCallback(async () => {
    // Early-return on `!_hasMore` (line 90-92). The `isFetching` guard is the
    // web equivalent of Flutter's per-key `_dataFetchNotifier.setFetching`
    // in-flight gate — it prevents overlapping pages on rapid scroll.
    if (!hasMoreRef.current || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsFetching(true);

    try {
      const result = await fetchContentLikedUsersUseCase.execute({
        mediaPath,
        currentUser: currentUserRef.current,
        cursor: cursorRef.current,
      });

      // Port the `_items.add(...)` append loop (lines 101-108) with dedup by
      // `userId` — newest-like-first order is preserved by appending the new
      // page after the existing rows.
      setLikedUsers((prev) => {
        const existingIds = new Set(prev.map((row) => row.userId));
        const additions = result.likedUsers.filter(
          (row) => !existingIds.has(row.userId),
        );
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });

      cursorRef.current = result.cursor;
      hasMoreRef.current = result.hasMore;
      setHasMore(result.hasMore);
    } catch (error) {
      appLogger.error('Error fetching content liked users', 'useContentLikedUsers.loadMore', {
        error: error as Error,
      });
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsFirstLoad(false);
    }
  }, [mediaPath]);

  // Initial load once on mount, mirroring Flutter's post-frame `getData`
  // (content_users_liked_bottom_sheet.dart:55, 80-82).
  useEffect(() => {
    void loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    likedUsers,
    hasMore,
    isFetching,
    isFirstLoad,
    loadMore,
  };
}
